import React from "react";
import { ArrowRight, Quote, Calendar } from "lucide-react";
import { PHOTOS } from "@/lib/site-config";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import usePageMeta from "@/lib/use-page-meta";

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section className="relative pt-28 pb-16 lg:pt-40 lg:pb-20 overflow-hidden editorial">
    <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
    <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
      <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium" data-testid="success-eyebrow">
        Client success
      </div>
      <h1 className="font-serif text-[2.6rem] sm:text-[3.4rem] lg:text-[4.2rem] text-[var(--navy)] mt-6 leading-[1.02]" data-testid="success-headline">
        Real people. Real moves.
        <br />
        <span className="underline-swoosh">Real visas in hand.</span>
      </h1>
      <p className="mt-7 text-lg lg:text-xl text-[var(--ink-soft)] max-w-2xl leading-relaxed">
        Every story below is a real client who started exactly where you are now — researching,
        second-guessing, wondering where to start. Here's what happened when they got proper support.
      </p>
    </div>
  </section>
);

/* ---------------- GLEN ---------------- */
const GlenStory = () => (
  <section className="py-16 lg:py-20 editorial">
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-5 gap-6 rounded-[2rem] overflow-hidden bg-[var(--navy)] text-[var(--cream)] border border-[var(--gold)]/30 shadow-2xl" data-testid="success-glen">
        <div className="lg:col-span-2 relative min-h-[340px] lg:min-h-0">
          <img src={PHOTOS.carley_clients} alt="Carley with clients Glen and partner" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[var(--navy)]/60 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-xs uppercase tracking-[0.25em]">
            <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />
            Met with Carley in September
          </div>
        </div>
        <figure className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
          <Quote className="w-9 h-9 text-[var(--gold)]" />
          <blockquote className="mt-5 font-serif text-xl lg:text-2xl leading-[1.4]">
            "THANK YOU for helping us!!! If you're considering meeting with anyone to discuss
            moving to Spain, I highly recommend Carley. She is awesome! We met with her in
            September and she <em className="text-[var(--gold)] not-italic font-medium">knows her stuff</em> — well organised and informed! We are well
            on our way to life in Spain thanks to her. She is an excellent relocation specialist!"
          </blockquote>
          <figcaption className="mt-8 pt-6 border-t border-[var(--cream)]/15">
            <div className="font-medium text-lg">Glen</div>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--gold)] mt-1">@happydayglen · Moving to Spain</div>
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
);

/* ---------------- CRAIG ---------------- */
const CraigStory = () => (
  <section className="py-16 lg:py-20 bg-[var(--cream-deep)]/60 editorial">
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-5 gap-6 rounded-[2rem] overflow-hidden bg-white border border-black/5 shadow-xl" data-testid="success-craig">
        <div className="lg:col-span-2 relative min-h-[380px] lg:min-h-0 order-first lg:order-last">
          <img
            src={PHOTOS.craig_visa}
            alt="Craig Swanson holding his approved Spanish visa"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/50 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--cream)]">
            <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />
            Visa in hand · Edinburgh → Spain
          </div>
        </div>
        <div className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
          <div className="font-serif text-4xl lg:text-5xl text-[var(--terracotta)] leading-none tracking-tight">
            If I can,<br />you can.
          </div>
          <div className="mt-4 text-xs uppercase tracking-[0.25em] text-[var(--muted)]">
            Craig Swanson · Edinburgh → Spain
          </div>
          <blockquote className="mt-7 font-serif text-lg lg:text-xl leading-[1.55] text-[var(--navy)] space-y-4">
            <p>
              "Tonight I assembled my pack for my Spanish Visa application. After multiple text
              messages, video calls, emails and a few frustrating moments (and a quick holiday in
              Spain), the moment has arrived — Wednesday, Edinburgh, 11am."
            </p>
            <p>
              "I cannot thank Carley enough. I opted for her hand-hold service, and that is
              exactly what she did — the attention to detail, support and going the extra mile
              was amazing. February, the dream, Wednesday, it's here."
            </p>
            <p className="text-[var(--terracotta)] italic">
              "If you are thinking about it — should I, could I, what if, or just don't know
              where to start — reach out to her and have the chat. Thank you, Carley Chaplin."
            </p>
          </blockquote>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- NEIL & CLARE ---------------- */
const NeilClareStory = () => (
  <section className="py-16 lg:py-20 editorial">
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <figure className="rounded-[2rem] overflow-hidden bg-white text-[var(--ink)] shadow-xl border border-black/5" data-testid="success-neil-clare">
        <div className="grid md:grid-cols-5">
          <div className="md:col-span-2 relative min-h-[320px] md:min-h-[440px] bg-[var(--cream-deep)]">
            <img
              src="https://customer-assets.emergentagent.com/job_elegant-landing-site/artifacts/ggtwupm3_a06e74d1-7604-4345-8d34-7b0eb87c4d2a.JPG"
              alt="Neil and Clare holding up their British passports after their Spanish NLV approval"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-3 p-6 lg:p-8 flex flex-col">
            <div className="flex items-center gap-3 pb-4 border-b border-black/5">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[var(--terracotta)] to-[var(--gold)] flex items-center justify-center text-[var(--cream)] font-serif text-lg">
                G
              </div>
              <div className="flex-1">
                <div className="font-semibold text-[var(--navy)] text-sm">Get To Spain</div>
                <div className="text-xs text-[var(--muted)]">Neil Saunders · 19 March</div>
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-[var(--terracotta)] font-medium hidden sm:inline">
                Manchester BLS
              </span>
            </div>
            <blockquote className="mt-5 text-[var(--ink)] leading-relaxed text-[0.98rem] flex-1">
              "From our initial call to <span className="text-[#1B74E4] font-medium">Carley Chaplin</span> in
              mid January, all the help you gave us, to our appointment at the Manchester BLS on
              the 19th March. Today, <strong>8 weeks to the day</strong>, we received our Spanish
              NLV Visas — now our dream begins. We couldn't recommend Carley enough, very
              thorough and meticulous, and made the process easier. Thank you 🤗 ✈️ 🏝️ 🌞"
            </blockquote>
            <div className="mt-5 pt-4 border-t border-black/5 text-xs text-[var(--muted)] flex items-center justify-between">
              <span>Neil &amp; Clare and others</span>
              <span>via Messenger</span>
            </div>
          </div>
        </div>
      </figure>
    </div>
  </section>
);

/* ---------------- HANNAH & GREG ---------------- */
const HannahGregStory = () => (
  <section className="py-16 lg:py-20 bg-[var(--cream-deep)]/60 editorial">
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-5 gap-6 rounded-[2rem] overflow-hidden bg-[var(--navy)] text-[var(--cream)] border border-[var(--gold)]/30 shadow-2xl" data-testid="success-hannah-greg">
        <div className="lg:col-span-2 relative min-h-[340px] lg:min-h-0">
          <img
            src={PHOTOS.hannah_greg}
            alt="Greg, Hannah and their two children at the airport on their way to Barcelona"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/60 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-xs uppercase tracking-[0.25em]">
            <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />
            Off to Barcelona · EU Approved
          </div>
        </div>
        <figure className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
          <Quote className="w-9 h-9 text-[var(--gold)]" />
          <blockquote className="mt-5 font-serif text-2xl lg:text-3xl leading-[1.35]">
            "That's exactly what we want. Just{" "}
            <em className="text-[var(--gold)] not-italic font-medium">peace of mind</em> and telling
            us what we need to do."
          </blockquote>
          <figcaption className="mt-8 pt-6 border-t border-[var(--cream)]/15">
            <div className="font-medium text-lg">Greg &amp; Hannah</div>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--gold)] mt-1">
              Moved to Barcelona with their two children
            </div>
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
);

/* ---------------- CARL & TERESA ---------------- */
const CarlTeresaStory = () => (
  <section className="py-16 lg:py-20 editorial">
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <figure className="rounded-[2rem] p-8 lg:p-12 bg-white border border-black/5 shadow-xl relative overflow-hidden" data-testid="success-carl-teresa">
        <div className="absolute -top-10 -right-10 w-56 h-56 bg-[var(--gold)]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <Quote className="w-9 h-9 text-[var(--terracotta)]" />
          <blockquote className="mt-5 font-serif text-lg lg:text-xl leading-[1.55] text-[var(--navy)]">
            "Carley is very knowledgeable and passionate in assisting people to relocate to Spain.
            We had a very constructive 1:1 meeting with Carley and as the result we intend on
            relocating to Spain in January 2027, using Carley's service and assistance. Carley has
            already highlighted significant hurdles for my wife and I to clear but with Carley's
            guidance and strategy going forward I am confident we will be resident in España in
            2027. <em className="text-[var(--terracotta)]">Muchas gracias.</em>"
          </blockquote>
          <figcaption className="mt-8 pt-6 border-t border-black/10">
            <div className="font-medium text-lg text-[var(--navy)]">Carl &amp; Teresa</div>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--terracotta)] mt-1">
              Relocating to Spain in 2027 · EU Approved
            </div>
          </figcaption>
        </div>
      </figure>
    </div>
  </section>
);

/* ---------------- PULL QUOTE ---------------- */
const PullQuote = () => (
  <section className="py-20 lg:py-24 bg-[var(--navy)] text-[var(--cream)] editorial grain relative overflow-hidden">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <Quote className="w-10 h-10 text-[var(--gold)] mx-auto" />
      <p className="mt-6 font-serif italic text-2xl lg:text-3xl leading-[1.4]">
        "Every one of these clients started with the same questions you have right now. The
        difference wasn't luck — it was clarity, structure and doing things in the right order."
      </p>
      <p className="mt-5 text-sm uppercase tracking-[0.25em] text-[var(--gold)]">— Carley Chaplin</p>
    </div>
  </section>
);

/* ---------------- BOOK CTA ---------------- */
const BookCTA = () => (
  <section className="py-24 lg:py-28 editorial" data-testid="success-book-cta">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.05]">
        Ready to write <em className="text-[var(--terracotta)]">your success story?</em>
      </h2>
      <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-2xl mx-auto">
        Book a relaxed, honest call with Carley — we'll look at your circumstances and map out what
        your next steps could look like.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-4">
        <a
          href="/nlv-approved#book"
          className="pill inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
          data-testid="success-cta-book"
        >
          <Calendar className="w-5 h-5" /> Book my call
        </a>
        <a
          href="/#programmes"
          className="pill inline-flex items-center gap-2 px-8 py-4 rounded-full border border-[var(--navy)]/25 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--cream)] font-medium"
          data-testid="success-cta-programmes"
        >
          Explore the programmes <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */
export default function ClientSuccess() {
  usePageMeta(
    "Client Success Stories | Get to Spain",
    "Real clients, real moves to Spain. Read how Carley Chaplin helped Glen, Craig, Neil and Clare plan and complete their moves — then book your own call."
  );
  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)] selection:bg-[var(--terracotta)] selection:text-[var(--cream)]" data-testid="client-success-page">
      <SiteNav />
      <Hero />
      <GlenStory />
      <CraigStory />
      <NeilClareStory />
      <HannahGregStory />
      <CarlTeresaStory />
      <PullQuote />
      <BookCTA />
      <SiteFooter />
    </main>
  );
}
